import React, { Suspense } from "react";
import { Route } from "react-router-dom";
import ProtectedRoute from "../routes/ProtectedRoute";
import { MainNav } from '../Layout/AppNav/NavItems';
import { useAuth } from '../auth/AuthContext';
import { resolveRole } from '../auth/roles';

const Fallback = () => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "rgba(255,255,255,0.6)",
      zIndex: 2000,
    }}
  >
    <div className="spinner-border text-primary" role="status" aria-label="Loading" />
  </div>
);

/**
 * Recursively extract all routes from navigation items
 * @param {Array} navItems - Navigation items to process
 * @returns {Array} Array of route objects with path, component, and allowedRoles
 */
const extractRoutesFromNav = (navItems) => {
  const routes = [];
  const pathMap = new Map(); // To handle duplicate paths with different roles
  
  for (const item of navItems) {
    // If item has a 'to' property, it's a route
    if (item.to && !item.external && item.component) {
      const path = item.to;
      const allowedRoles = item.allowedRoles || [];
      const rolesToComponents = item.rolesToComponents || allowedRoles.reduce(
        (acc, role) => ({ ...acc, [role]: item.component }),
        {}
      );

      if (pathMap.has(path)) {
        // If path already exists, merge the roles and components
        const existing = pathMap.get(path);
        existing.allowedRoles = [...new Set([...existing.allowedRoles, ...allowedRoles])];
        existing.components = [...(existing.components || [existing.component]), item.component];
        existing.rolesToComponents = {
          ...existing.rolesToComponents,
          ...rolesToComponents,
        };
      } else {
        // New path
        pathMap.set(path, {
          path,
          component: item.component,
          allowedRoles,
          components: [item.component],
          rolesToComponents,
        });
      }
    }
    
    // Recursively process nested content
    if (item.content && Array.isArray(item.content)) {
      const nestedRoutes = extractRoutesFromNav(item.content);
      nestedRoutes.forEach(route => {
        const path = route.path;
        const allowedRoles = route.allowedRoles || [];
        
        if (pathMap.has(path)) {
          const existing = pathMap.get(path);
          existing.allowedRoles = [...existing.allowedRoles, ...allowedRoles];
          existing.components = [...(existing.components || [existing.component]), route.component];
          existing.rolesToComponents = {
            ...existing.rolesToComponents,
            ...allowedRoles.reduce((acc, role) => ({ ...acc, [role]: route.component }), {})
          };
        } else {
          pathMap.set(path, {
            path,
            component: route.component,
            allowedRoles,
            components: [route.component],
            rolesToComponents: allowedRoles.reduce((acc, role) => ({ ...acc, [role]: route.component }), {})
          });
        }
      });
    }
  }
  
  return Array.from(pathMap.values());
};

/**
 * Dynamic component that renders the correct component based on user role
 */
const RoleBasedComponent = ({ rolesToComponents, allowedRoles }) => {
  const { roleType } = useAuth();
  
  console.log('RoleBasedComponent - roleType:', roleType);
  console.log('RoleBasedComponent - rolesToComponents:', rolesToComponents);
  console.log('RoleBasedComponent - allowedRoles:', allowedRoles);
  
  // Find the component for the current user's role (supports CRYR role aliases)
  const Component = rolesToComponents[roleType] || rolesToComponents[resolveRole(roleType)];
  
  if (!Component) {
    console.log('No component found for role:', roleType);
    // Fallback to the first available component if role not found
    const firstComponent = Object.values(rolesToComponents)[0];
    return firstComponent ? <firstComponent /> : <div>Access Denied - No component found for role: {roleType}</div>;
  }
  
  console.log('Rendering component for role:', roleType);
  return <Component />;
};

/**
 * Generate Route components from navigation items
 * @returns {Array} Array of Route components
 */
export const generateRoutesFromNav = () => {
  const routes = extractRoutesFromNav(MainNav);
  
  // Debug: Log the generated routes
  console.log('Generated routes:', routes);
  console.log('Routes with role-based components:', routes.filter(r => r.rolesToComponents));
  
  return routes.map(({ path, allowedRoles, rolesToComponents, component: Component }) => {
    // Routes that have nested routing need a trailing * to allow child routes
    const routesWithNestedRouting = [
      '/forms',
      '/components', 
      '/charts',
      '/tables',
      '/elements',
      '/widgets',
      '/pages',
      '/apps',
      '/dashboards'
    ];
    
    const needsTrailingStar = routesWithNestedRouting.some(route => path.startsWith(route));
    const routePath = needsTrailingStar ? `${path}/*` : path;
    
    
    return (
      <Route
        key={path}
        path={routePath}
        element={
          <ProtectedRoute allowedRoles={allowedRoles}>
            <Suspense fallback={<Fallback />}>
              {rolesToComponents ? (
                <RoleBasedComponent 
                  rolesToComponents={rolesToComponents} 
                  allowedRoles={allowedRoles} 
                />
              ) : (
                <Component />
              )}
            </Suspense>
          </ProtectedRoute>
        }
      />
    );
  });
};

/**
 * Get allowed roles for a specific path (for manual routes that aren't in nav)
 * @param {string} path - The route path
 * @returns {Array} Array of allowed roles
 */
export const getRouteRoles = (path) => {
  const findRouteInNav = (navItems) => {
    for (const item of navItems) {
      if (item.to === path) {
        return item.allowedRoles || [];
      }
      
      if (item.content && Array.isArray(item.content)) {
        const found = findRouteInNav(item.content);
        if (found.length > 0) {
          return found;
        }
      }
    }
    return [];
  };

  return findRouteInNav(MainNav);
};
